import React, { useState, useEffect, useRef } from "react";
import { Input, Button, Avatar, List, Dropdown, Modal, Alert, Spin, Tag } from "antd";
import {
  SendOutlined,
  UserOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
  ArrowUpOutlined,
  LockOutlined,
  ArrowLeftOutlined,
  CheckOutlined
} from "@ant-design/icons";
import { useCommunication } from "../hooks/useCommunication.js";
import { isAuthenticated } from "../../Auth/auth";
import "./CommunicationView.css";

const { TextArea } = Input;

export const CommunicationView = ({
  conversationId,
  title = "Chat",
  receiver = null,
  onBack = null
}) => {
  const currentUser = isAuthenticated();
  const currentUserId = String(currentUser?._id || "");
  const messagesContainerRef = useRef(null);
  const prevMessagesCountRef = useRef(0);
  const isInitialLoadRef = useRef(true);

  const [inputText, setInputText] = useState("");
  const [editingMessage, setEditingMessage] = useState(null);
  const [editText, setEditText] = useState("");

  const {
    messages,
    hasMore,
    loading,
    loadingOlder,
    sending,
    error,
    isConnected,
    loadOlderMessages,
    sendMessage,
    editMessage,
    deleteMessage
  } = useCommunication({ conversationId });

  // Reset initial load flag when conversationId changes
  useEffect(() => {
    isInitialLoadRef.current = true;
    prevMessagesCountRef.current = 0;
  }, [conversationId]);

  // Auto scroll within message container only (prevents whole window from shifting)
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    if (loadingOlder) {
      return;
    }

    const currentCount = messages.length;
    const prevCount = prevMessagesCountRef.current;

    if (isInitialLoadRef.current && currentCount > 0) {
      container.scrollTop = container.scrollHeight;
      isInitialLoadRef.current = false;
      prevMessagesCountRef.current = currentCount;
      return;
    }

    if (currentCount > prevCount) {
      const isNearBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight < 200;
      const lastMsg = messages[messages.length - 1];
      const isMyMessage = lastMsg && String(lastMsg.senderIdentityId) === currentUserId;

      if (isNearBottom || isMyMessage) {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: "smooth"
        });
      }
    }

    prevMessagesCountRef.current = currentCount;
  }, [messages, loadingOlder, currentUserId]);

  const handleSend = async () => {
    if (!inputText.trim() || sending) return;
    const textToSend = inputText;
    setInputText("");
    try {
      await sendMessage(textToSend);
    } catch {
      // Error handled in hook state
      setInputText(textToSend); // Restore if failed
    }
  };

  const handleKeyDown = e => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const openEditModal = message => {
    setEditingMessage(message);
    setEditText(message.text || "");
  };

  const handleSaveEdit = async () => {
    if (!editingMessage || !editText.trim()) return;
    try {
      await editMessage(editingMessage._id, editText.trim());
      setEditingMessage(null);
      setEditText("");
    } catch {
      // Error handled in hook
    }
  };

  const handleDelete = message => {
    Modal.confirm({
      title: "Nachricht löschen?",
      content: "Möchten Sie diese Nachricht wirklich löschen?",
      okText: "Löschen",
      okType: "danger",
      cancelText: "Abbrechen",
      onOk: async () => {
        try {
          await deleteMessage(message._id);
        } catch {
          // Handled in hook
        }
      }
    });
  };

  const renderMessageActions = message => {
    if (message.status === "SOFT_DELETED") return null;

    const isMine = String(message.senderIdentityId) === currentUserId;
    const canDelete = isMine || currentUser?.role === 2 || currentUser?.role === 3 || currentUser?.role === 4;

    const items = [];
    if (isMine) {
      items.push({
        key: "edit",
        icon: <EditOutlined />,
        label: "Bearbeiten",
        onClick: () => openEditModal(message)
      });
    }
    if (canDelete) {
      items.push({
        key: "delete",
        icon: <DeleteOutlined />,
        label: "Löschen",
        danger: true,
        onClick: () => handleDelete(message)
      });
    }

    if (items.length === 0) return null;

    return (
      <Dropdown menu={{ items }} trigger={["click"]}>
        <Button
          type="text"
          size="small"
          icon={<MoreOutlined />}
          className="comm-msg-more-btn"
          aria-label="Optionen"
        />
      </Dropdown>
    );
  };

  const avatarSrc = receiver?.picture?.url || (typeof receiver?.picture === "string" ? receiver.picture : null);
  const displayTitle = receiver?.firstName
    ? `${receiver.firstName} ${receiver.lastName || ""}`.trim()
    : (receiver?.name || title);

  return (
    <div className="communication-view">
      {/* Header */}
      <div className="communication-header">
        <div className="communication-header-left">
          {onBack && (
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={onBack}
              className="comm-back-btn"
              aria-label="Zurück"
            />
          )}
          <Avatar
            size={40}
            src={avatarSrc}
            icon={!avatarSrc && <UserOutlined />}
            className="comm-header-avatar"
          />
          <div className="communication-header-info">
            <div className="communication-title">{displayTitle}</div>
            <div className="communication-status">
              <span className={`comm-status-dot ${isConnected ? "" : "disconnected"}`} />
              <span>{isConnected ? "Aktiv" : "Verbindung wird hergestellt..."}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Error alert */}
      {error && (
        <Alert
          message={error}
          type="warning"
          showIcon
          closable
          className="comm-alert"
        />
      )}

      {/* Messages Scroll Area */}
      <div className="communication-messages-area" ref={messagesContainerRef}>
        {/* Cursor Pagination: Load Older */}
        {hasMore && (
          <div className="comm-load-older">
            <Button
              type="text"
              icon={loadingOlder ? <Spin size="small" /> : <ArrowUpOutlined />}
              onClick={loadOlderMessages}
              disabled={loadingOlder}
              className="comm-load-older-btn"
            >
              {loadingOlder ? "Laden..." : "Ältere Nachrichten laden"}
            </Button>
          </div>
        )}

        {loading ? (
          <div className="comm-loading-center">
            <Spin size="large" />
          </div>
        ) : messages.length === 0 ? (
          <div className="comm-empty-state">
            <div className="comm-empty-icon-wrap">
              <LockOutlined />
            </div>
            <div className="comm-empty-title">Ende-zu-Ende geschützte Konversation</div>
            <p className="comm-empty-subtitle">
              Senden Sie eine Nachricht, um das Gespräch zu beginnen. Ihre Nachrichten sind sicher übertragen.
            </p>
          </div>
        ) : (
          <List
            dataSource={messages}
            split={false}
            renderItem={msg => {
              const isMine = String(msg.senderIdentityId) === currentUserId;
              const isDeleted = msg.status === "SOFT_DELETED";
              const senderName =
                msg.senderSnapshot?.displayName || (isMine ? "Ich" : "Teilnehmer");

              const timeStr = msg.sentAt
                ? new Date(msg.sentAt).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit"
                  })
                : "";

              return (
                <div
                  key={msg._id}
                  className={`comm-message-row ${isMine ? "comm-mine" : "comm-theirs"}`}
                >
                  <Avatar
                    size={32}
                    icon={<UserOutlined />}
                    className={`comm-avatar ${isMine ? "comm-avatar-mine" : "comm-avatar-theirs"}`}
                  />
                  <div className="comm-message-content">
                    {!isMine && <span className="comm-sender-name">{senderName}</span>}

                    <div className={`comm-bubble ${isDeleted ? "comm-bubble-deleted" : ""}`}>
                      {isDeleted ? (
                        <span className="comm-deleted-text">
                          [Diese Nachricht wurde gelöscht]
                        </span>
                      ) : (
                        <span>{msg.text}</span>
                      )}

                      {msg.editedAt && !isDeleted && (
                        <Tag className="comm-edited-tag">bearbeitet</Tag>
                      )}

                      <div className="comm-bubble-footer">
                        <span>{timeStr}</span>
                        {isMine && !isDeleted && (
                          <CheckOutlined className="comm-check-icon" />
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="comm-message-actions">
                    {renderMessageActions(msg)}
                  </div>
                </div>
              );
            }}
          />
        )}
      </div>

      {/* Composer */}
      <div className="communication-composer">
        <TextArea
          value={inputText}
          onChange={e => setInputText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Nachricht eingeben..."
          autoSize={{ minRows: 1, maxRows: 4 }}
          disabled={!conversationId}
          className="comm-textarea"
        />
        <Button
          type="primary"
          icon={<SendOutlined />}
          onClick={handleSend}
          loading={sending}
          disabled={!inputText.trim() || !conversationId}
          className="comm-send-button"
          aria-label="Senden"
        />
      </div>

      {/* Edit Modal */}
      <Modal
        title="Nachricht bearbeiten"
        open={Boolean(editingMessage)}
        onOk={handleSaveEdit}
        onCancel={() => setEditingMessage(null)}
        okText="Speichern"
        cancelText="Abbrechen"
      >
        <TextArea
          value={editText}
          onChange={e => setEditText(e.target.value)}
          autoSize={{ minRows: 2, maxRows: 6 }}
          className="comm-textarea"
        />
      </Modal>
    </div>
  );
};
